/**
 * Lead Command Center — the reusable Record Interaction flow.
 *
 * This is the ONE write path for working a lead: what happened, the outcome,
 * what should happen next, and when the lead returns to LeadLoop. Nothing
 * submits until "Update Lead" — the Worker then logs a lead_events record
 * (with previous next-step/return-date values in metadata) and updates the
 * lead atomically.
 *
 * Contexts prefill it: the verified checkmark locks Outcome = Verified,
 * the loop-card Note preselects Internal Note, Call/Email actions preselect
 * their interaction type.
 */

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  lccApi,
  type LccInteractionOutcome,
  type LccInteractionType,
  type LccLead,
  type LccVerificationMethod,
} from '@/lib/lcc-api';
import { useLccInvalidate } from './lead-actions';
import { formatLccDateTime, formatLccLabel } from './lcc-shared';

const INTERACTION_TYPES: Array<{ value: LccInteractionType; label: string }> = [
  { value: 'call', label: 'Call' },
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'in_person', label: 'In Person' },
  { value: 'new_info', label: 'New Info' },
  { value: 'internal_note', label: 'Internal Note' },
];

const OUTCOME_LABELS: Record<LccInteractionOutcome, string> = {
  no_change: 'No Change',
  verified: 'Verified',
  waiting_on_lead: 'Waiting on Lead',
  callback_needed: 'Callback Needed',
  appointment_set: 'Appointment Set',
  estimate_sent: 'Estimate Sent',
  converted: 'Converted',
  lost: 'Lost',
  unverified: 'Remove Verification',
};

/** Outcomes offered in the normal select (unverified is checkmark-flow only). */
const NORMAL_OUTCOMES: LccInteractionOutcome[] = [
  'no_change',
  'verified',
  'waiting_on_lead',
  'callback_needed',
  'appointment_set',
  'estimate_sent',
  'converted',
  'lost',
];

const VERIFICATION_METHODS: Array<{ value: LccVerificationMethod; label: string }> = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'text', label: 'Text' },
  { value: 'in_person', label: 'In Person' },
  { value: 'ai_receptionist', label: 'AI Receptionist' },
  { value: 'other', label: 'Other' },
];

/** Interaction types that double as a verification method. */
const TYPE_TO_METHOD: Partial<Record<LccInteractionType, LccVerificationMethod>> = {
  call: 'call',
  text: 'text',
  email: 'email',
  in_person: 'in_person',
};

type ReturnChoice = '2h' | 'tomorrow' | '3d' | '5d' | '10d' | 'manual';

const RETURN_CHOICES: Array<{ value: ReturnChoice; label: string }> = [
  { value: '2h', label: '2 Hours' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: '3d', label: '3 Days' },
  { value: '5d', label: '5 Days' },
  { value: '10d', label: '10 Days' },
  { value: 'manual', label: 'Manual Date' },
];

function resolveReturnAt(choice: ReturnChoice | null, manualDate: string): string | undefined {
  if (!choice) return undefined;
  const now = new Date();
  switch (choice) {
    case '2h':
      return new Date(now.getTime() + 2 * 3_600_000).toISOString();
    case 'tomorrow': {
      const d = new Date(now);
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d.toISOString();
    }
    case '3d':
    case '5d':
    case '10d': {
      const days = { '3d': 3, '5d': 5, '10d': 10 }[choice];
      return new Date(now.getTime() + days * 86_400_000).toISOString();
    }
    case 'manual': {
      const ts = Date.parse(manualDate);
      return Number.isNaN(ts) ? undefined : new Date(ts).toISOString();
    }
  }
}

export interface RecordInteractionDefaults {
  interactionType?: LccInteractionType;
  outcome?: LccInteractionOutcome;
  /** Verify/un-verify contexts pin the outcome. */
  lockOutcome?: boolean;
  /** Un-verify requires an explanation. */
  notesRequired?: boolean;
}

export function RecordInteractionForm({
  lead,
  defaults,
  onDone,
}: {
  lead: LccLead;
  defaults?: RecordInteractionDefaults;
  onDone?: () => void;
}) {
  const invalidate = useLccInvalidate();
  const [interactionType, setInteractionType] = useState<LccInteractionType>(
    defaults?.interactionType ?? 'call',
  );
  const [notes, setNotes] = useState('');
  const [outcome, setOutcome] = useState<LccInteractionOutcome>(defaults?.outcome ?? 'no_change');
  const [method, setMethod] = useState<LccVerificationMethod | null>(null);
  const [nextStep, setNextStep] = useState(lead.nextStep ?? '');
  const [returnChoice, setReturnChoice] = useState<ReturnChoice | null>(null);
  const [manualDate, setManualDate] = useState('');

  const closing = outcome === 'converted' || outcome === 'lost';
  const effectiveMethod = method ?? TYPE_TO_METHOD[interactionType] ?? 'other';
  const notesMissing = Boolean(defaults?.notesRequired) && !notes.trim();

  const outcomeOptions = useMemo(() => {
    if (defaults?.lockOutcome && defaults.outcome && !NORMAL_OUTCOMES.includes(defaults.outcome)) {
      return [defaults.outcome];
    }
    return NORMAL_OUTCOMES;
  }, [defaults]);

  const mutation = useMutation({
    mutationFn: () =>
      lccApi.recordInteraction(lead.id, {
        interactionType,
        notes: notes.trim() || undefined,
        outcome,
        verificationMethod: outcome === 'verified' ? effectiveMethod : undefined,
        nextStep: closing ? undefined : nextStep.trim() || undefined,
        nextFollowUpAt: closing ? undefined : resolveReturnAt(returnChoice, manualDate),
      }),
    onSuccess: () => {
      invalidate();
      setNotes('');
      setReturnChoice(null);
      setManualDate('');
      onDone?.();
    },
  });

  const sectionLabel = 'text-[11px] font-medium uppercase tracking-wide text-muted-foreground';

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className={sectionLabel}>Interaction Type</div>
        <div className="flex flex-wrap gap-1">
          {INTERACTION_TYPES.map((type) => (
            <Button
              key={type.value}
              type="button"
              size="sm"
              variant={interactionType === type.value ? 'default' : 'outline'}
              className="h-7 px-2.5 text-[12px]"
              onClick={() => setInteractionType(type.value)}
            >
              {type.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className={sectionLabel}>Notes{defaults?.notesRequired ? ' (required)' : ''}</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Describe the interaction, outcome, or new information..."
          className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="space-y-1.5">
          <div className={sectionLabel}>Outcome</div>
          <Select
            value={outcome}
            onValueChange={(value) => setOutcome(value as LccInteractionOutcome)}
            disabled={defaults?.lockOutcome}
          >
            <SelectTrigger className="h-8 w-[180px] text-[12px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {outcomeOptions.map((value) => (
                <SelectItem key={value} value={value}>
                  {OUTCOME_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {outcome === 'verified' && (
          <div className="space-y-1.5">
            <div className={sectionLabel}>Verified via</div>
            <Select
              value={effectiveMethod}
              onValueChange={(value) => setMethod(value as LccVerificationMethod)}
            >
              <SelectTrigger className="h-8 w-[160px] text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VERIFICATION_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!closing && (
        <div className="space-y-1.5">
          <div className={sectionLabel}>Next Step</div>
          <textarea
            value={nextStep}
            onChange={(e) => setNextStep(e.target.value)}
            rows={2}
            placeholder="What should happen next when this lead returns to LeadLoop?"
            className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <div className={sectionLabel}>Return to LeadLoop</div>
        {closing ? (
          <p className="text-[12px] text-muted-foreground">
            No scheduled return — this lead is closing as {OUTCOME_LABELS[outcome]}.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1">
              {RETURN_CHOICES.map((choice) => (
                <Button
                  key={choice.value}
                  type="button"
                  size="sm"
                  variant={returnChoice === choice.value ? 'default' : 'outline'}
                  className="h-7 px-2.5 text-[12px]"
                  onClick={() =>
                    setReturnChoice((prev) => (prev === choice.value ? null : choice.value))
                  }
                >
                  {choice.label}
                </Button>
              ))}
            </div>
            {returnChoice === 'manual' && (
              <input
                type="datetime-local"
                value={manualDate}
                onChange={(e) => setManualDate(e.target.value)}
                className="flex h-8 rounded-md border border-input bg-background px-2.5 text-[12px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            )}
          </>
        )}
      </div>

      {mutation.isError && (
        <p className="text-[12px] text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'Update failed.'}
        </p>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={mutation.isPending || notesMissing || (returnChoice === 'manual' && !manualDate)}
          onClick={() => mutation.mutate()}
          title={notesMissing ? 'Add a note first' : undefined}
        >
          {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />}
          Update Lead
        </Button>
      </div>
    </div>
  );
}

export function RecordInteractionDialog({
  lead,
  open,
  onOpenChange,
  defaults,
  title,
}: {
  lead: LccLead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaults?: RecordInteractionDefaults;
  title?: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title ?? 'Record Interaction'}</DialogTitle>
          <DialogDescription>{lead.title}</DialogDescription>
        </DialogHeader>
        {open && (
          <RecordInteractionForm lead={lead} defaults={defaults} onDone={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The verified checkmark: green when verified, gray when not. Clicking never
 * toggles silently — it opens Record Interaction with verification context.
 */
export function VerifiedCheck({ lead, className }: { lead: LccLead; className?: string }) {
  const [open, setOpen] = useState(false);
  const verified = lead.verified;
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        title={
          verified
            ? `Verified via ${formatLccLabel(lead.verifiedMethod ?? 'other')} — ${formatLccDateTime(lead.verifiedAt)}`
            : 'Not verified yet — click to verify'
        }
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full transition-colors hover:opacity-80',
          className,
        )}
        aria-label={verified ? 'Verified — click to review' : 'Not verified — click to verify'}
      >
        <CheckCircle2
          className={cn('h-[18px] w-[18px]', verified ? 'text-emerald-500' : 'text-muted-foreground/35')}
          aria-hidden
        />
      </button>
      <RecordInteractionDialog
        lead={lead}
        open={open}
        onOpenChange={setOpen}
        title={verified ? 'Remove verification' : 'Verify lead'}
        defaults={
          verified
            ? {
                interactionType: 'internal_note',
                outcome: 'unverified',
                lockOutcome: true,
                notesRequired: true,
              }
            : { interactionType: 'call', outcome: 'verified', lockOutcome: true }
        }
      />
    </>
  );
}
